/**
 * WhoopImportButton - Optional button to import WHOOP data into Daily Check-In
 * 
 * Pre-fills hours_slept and sleep_quality from WHOOP data
 * User can edit or overwrite values
 * Manual trigger only - no automatic behaviour
 * No backend/schema changes
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Activity, Check, Loader2 } from 'lucide-react';
import { useWhoopTodayData } from '@/hooks/useWhoopData';
import { toast } from 'sonner';

interface WhoopImportButtonProps {
  onImport: (data: { hours_slept?: number; sleep_quality?: number }) => void;
  disabled?: boolean;
}

export function WhoopImportButton({ onImport, disabled }: WhoopImportButtonProps) {
  const { todayData, isLoading } = useWhoopTodayData();
  const [imported, setImported] = useState(false);

  // Don't render if no WHOOP data available
  if (isLoading) {
    return (
      <Button variant="outline" size="sm" disabled className="gap-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading WHOOP...
      </Button>
    );
  }

  if (!todayData) {
    return null; // No WHOOP data, don't show button
  }

  const handleImport = () => {
    const importData: { hours_slept?: number; sleep_quality?: number } = {};

    // Convert sleep duration from minutes to hours
    if (todayData.sleep_duration_minutes) {
      importData.hours_slept = Math.round((todayData.sleep_duration_minutes / 60) * 10) / 10;
    }

    // Map sleep performance to sleep quality (1-10 scale)
    if (todayData.sleep_performance) {
      // Convert percentage to 1-10 scale
      importData.sleep_quality = Math.round(todayData.sleep_performance / 10);
    }

    if (Object.keys(importData).length === 0) {
      toast.error('No sleep data available from WHOOP');
      return;
    }

    onImport(importData);
    setImported(true);
    
    const parts = [];
    if (importData.hours_slept) parts.push(`${importData.hours_slept}h sleep`);
    if (importData.sleep_quality) parts.push(`quality: ${importData.sleep_quality}/10`);
    
    toast.success(`Imported from WHOOP: ${parts.join(', ')}`);
  };

  if (imported) {
    return (
      <Button variant="outline" size="sm" disabled className="gap-2 text-accent border-accent/30">
        <Check className="h-3.5 w-3.5" />
        WHOOP Data Imported
      </Button>
    );
  }

  return (
    <Button 
      variant="outline" 
      size="sm" 
      onClick={handleImport}
      disabled={disabled}
      className="gap-2 border-primary/30 hover:border-primary hover:bg-primary/10 transition-colors"
    >
      <Activity className="h-3.5 w-3.5 text-primary" />
      Import from WHOOP
    </Button>
  );
}

export default WhoopImportButton;
